import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ProductUid } from '@constants';
import { AnalyticsService, ContentService, GknFundamentalCardLink, ProductService } from '@services';
import { Observable, filter, map, switchMap, tap } from 'rxjs';
import { ResourceCenterReleaseNote } from './resource-center-release-note.interface';

@Injectable({ providedIn: 'root' })
export class HomeFundamentalsReleaseNotesService {
    private contentService = inject(ContentService);
    private httpClient = inject(HttpClient);
    private productService = inject(ProductService);

    /**
     * Provides Release Notes data to be displayed in UI.
     * @params productUid - id of given product.
     */
    get releaseNotes$(): Observable<{ links: GknFundamentalCardLink[]; action?: GknFundamentalCardLink }> {
        return this.productService.selectedProduct$.pipe(
            switchMap((product) => {
                switch (product.uid) {
                    case ProductUid.GENESYS_CLOUD_CX:
                        return this.genesysCloudReleaseNotes$;
                    default:
                        // content service already knows the selected product
                        return this.contentService.releaseNotes$.pipe(map((releaseNotes) => ({ links: releaseNotes })));
                }
            }),
            filter(
                (data: { links: GknFundamentalCardLink[]; action?: GknFundamentalCardLink }) => data?.links?.length > 0,
            ),
            tap((data) => console.debug('release notes', data)),
        );
    }

    /**
     * Provides Genesys Cloud release notes data from dedicate resource.
     */
    private get genesysCloudReleaseNotes$(): Observable<{
        links: GknFundamentalCardLink[];
        action: GknFundamentalCardLink;
    }> {
        const url = new URL('https://help.genesys.cloud/wp-json/wp/v2/releasenotes');
        url.searchParams.set(
            'filter',
            new URLSearchParams({
                releasenote_category: 'genesys-cloud',
            }).toString(),
        );

        return this.httpClient.get<ResourceCenterReleaseNote[]>(url.toString()).pipe(
            filter((res: ResourceCenterReleaseNote[]) => res?.length > 0 && !!res[0]),
            map((res: ResourceCenterReleaseNote[]) => res[0]),
            map((releaseNoteData: ResourceCenterReleaseNote) => {
                const links: GknFundamentalCardLink[] = [];
                const gcUrl = 'https://help.genesys.cloud/release-notes/genesys-cloud';
                [
                    {
                        label: 'Read the latest release notes',
                        url: gcUrl + '/',
                    },
                    {
                        label: 'Features coming soon',
                        url: gcUrl + '/features-coming-soon/',
                    },
                ].forEach(({ label, url }) => {
                    links.push({
                        label,
                        url,
                        dataAnalytics: ['release', 'notes', AnalyticsService.format(label)],
                    });
                });

                // Matches the title.rendered to:
                //   /              begin JavaScript regex
                //   .*             any number of characters 0-n
                //   (              capturing group
                //   \b             word boundry
                //   (?:            non-capturing group
                //   Jan...Dec      exact match text to full month
                //   )              close non-capturing group
                //                  literal space
                //   \d{1,2}        any digit 0-9, 1 to 2 occurrances
                //   ,              literal comma
                //                  literal space
                //   \d{4}          any digit 0-9, exactly 4 occurrances
                //   .*             any number of characters 0-n
                //   /g             end JavaScript regex, apply "globally" to the input
                const regex =
                    /.*((?:January|February|March|April|May|June|July|August|September|October|November|December) \d{1,2}, \d{4}).*/;

                const match = releaseNoteData?.title?.rendered?.match(regex);

                let callToAction = 'View All';
                let dataAnalytics = ['release', 'notes'];
                if (match) {
                    callToAction = match[1];
                    const date = new Date(callToAction);
                    dataAnalytics = [
                        ...dataAnalytics,
                        date.getFullYear().toString(),
                        (date.getMonth() + 1).toString(),
                        date.getDate().toString(),
                    ];
                } else {
                    dataAnalytics = [...dataAnalytics, 'view', 'most', 'recent'];
                }

                const action = {
                    url: releaseNoteData?.slug ? gcUrl + '/' + releaseNoteData.slug + '/' : '',
                    label: callToAction,
                    dataAnalytics: dataAnalytics,
                };

                return { links, action };
            }),
        );
    }
}
